import { useQuery } from '@tanstack/react-query';
import { City } from '@/types/location';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';

/**
 * Hook para buscar cidades de um estado via Edge Function
 * Cidades são carregadas sob demanda (lazy load) por serem ~150k registros
 */
export const useGlobalCities = (
  countryCode: string | null, 
  stateCode: string | null
) => {
  const queryResult = useQuery({
    queryKey: ['global-cities', countryCode, stateCode],
    queryFn: async (): Promise<City[]> => {
      if (!countryCode || !stateCode) {
        return [];
      }
      
      debugLogger.debug('useGlobalCities', `Fetching cities for ${countryCode}/${stateCode} from Edge Function...`);
      
      const { data, error } = await supabase.functions.invoke('global-cities', {
        body: { countryCode, stateCode },
      });

      if (error) {
        debugLogger.error('useGlobalCities', `Error fetching cities: ${error.message}`);
        throw error;
      }

      const cities: City[] = (data?.cities || []).map((city: any) => ({
        name: city.name,
        stateCode: city.stateCode || stateCode,
        countryCode: city.countryCode || countryCode,
        latitude: city.latitude || null,
        longitude: city.longitude || null,
      }));

      debugLogger.debug('useGlobalCities', `Loaded ${cities.length} cities for ${countryCode}/${stateCode}`);
      return cities;
    },
    enabled: !!countryCode && !!stateCode,
    staleTime: 30 * 60 * 1000, // 30 minutes - cities don't change often
    gcTime: 60 * 60 * 1000, // 1 hour in memory
    refetchOnWindowFocus: false,
  });

  return {
    ...queryResult,
    cities: queryResult.data || [],
  };
};
